/**
 * Adapter over real lunes-gate endpoints only.
 * credentials: 'include' for lunes_session cookie; 401 → /login.
 */
import { push, replace, spaPath, absoluteNextUrl } from './lib/historyNav.js'
import { opsUnitForMode, DEFAULT_MODE, isLiveOpsUnit } from './mode.js'
import { defaultPathForSite, SITE_ORIGINS } from './lib/hostSite.js'

const BETS_PAGE = 50
const DEFAULT_NEXT = defaultPathForSite()

function loginUrl(next) {
  let dest = ''
  if (next) {
    if (absoluteNextUrl(next)) {
      dest = String(next)
    } else {
      dest = spaPath(next, DEFAULT_NEXT) || DEFAULT_NEXT
    }
  }
  const q = dest ? `?next=${encodeURIComponent(dest)}` : ''
  return `/login${q}`
}

async function parseJson(res) {
  try {
    return await res.json()
  } catch {
    return null
  }
}

function handleUnauthorized(res) {
  if (res.status === 401) {
    const next = window.location.pathname + window.location.search
    replace(loginUrl(next))
    return true
  }
  return false
}

async function apiFetch(path, opts = {}) {
  const skipAuthRedirect = !!opts.skipAuthRedirect
  const { skipAuthRedirect: _drop, ...fetchOpts } = opts
  const res = await fetch(path, {
    credentials: 'include',
    cache: 'no-store',
    ...fetchOpts,
    headers: {
      Accept: 'application/json',
      ...(fetchOpts.body ? { 'Content-Type': 'application/json' } : {}),
      ...(fetchOpts.headers || {}),
    },
  })
  if (!skipAuthRedirect && handleUnauthorized(res)) {
    return { res, data: null, unauthorized: true }
  }
  const data = await parseJson(res)
  return { res, data, unauthorized: false }
}

function authNextPayload(next) {
  if (next == null || next === '') return DEFAULT_NEXT
  if (absoluteNextUrl(next)) return String(next)
  return spaPath(next, DEFAULT_NEXT) || DEFAULT_NEXT
}

/** POST /auth/login */
export async function login(username, password, next = DEFAULT_NEXT) {
  const { res, data } = await apiFetch('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ username, password, next: authNextPayload(next) }),
    skipAuthRedirect: true,
  })
  return { ok: res.ok && data && data.ok, res, data }
}

/** POST /auth/verify-otp — Telegram 2FA code */
export async function verifyOtp(code, next = DEFAULT_NEXT) {
  const { res, data } = await apiFetch('/auth/verify-otp', {
    method: 'POST',
    body: JSON.stringify({
      code: String(code || '').trim(),
      next: authNextPayload(next),
    }),
    skipAuthRedirect: true,
  })
  return { ok: res.ok && data && data.ok, res, data }
}

/** POST /auth/resend-otp */
export async function resendOtp(next = DEFAULT_NEXT) {
  const { res, data } = await apiFetch('/auth/resend-otp', {
    method: 'POST',
    body: JSON.stringify({ next: authNextPayload(next) }),
    skipAuthRedirect: true,
  })
  return { ok: res.ok && data && data.ok, res, data }
}

/** GET /auth/otp-status */
export async function otpStatus() {
  const { res, data } = await apiFetch('/auth/otp-status', { skipAuthRedirect: true })
  return { ok: !!(res.ok && data && data.ok), res, data }
}

/** Apex moon landing (not SPA /login). Product hosts → apex. */
function landingUrl() {
  const host = (window.location.hostname || '').toLowerCase()
  if (
    host === 'dashboard.lunes-crypto.ru' ||
    host === 'paper.lunes-crypto.ru' ||
    host === 'live.lunes-crypto.ru' ||
    host === 'lunes-crypto.ru' ||
    host === 'www.lunes-crypto.ru'
  ) {
    return `${SITE_ORIGINS.landing}/`
  }
  return '/'
}

/** POST /auth/logout → clear cookie, then land on apex (not /login). */
export async function logout() {
  await apiFetch('/auth/logout', { method: 'POST', skipAuthRedirect: true })
  window.location.replace(landingUrl())
}

/** GET /auth/sessions — active devices for current user */
export async function getSessions() {
  const { res, data, unauthorized } = await apiFetch('/auth/sessions')
  if (unauthorized) return { ok: false, unauthorized: true, res, data }
  return { ok: !!(res.ok && data && data.ok), res, data, unauthorized: false }
}

/** POST /auth/sessions/revoke — invalidate one jti */
export async function revokeSession(jti) {
  const { res, data, unauthorized } = await apiFetch('/auth/sessions/revoke', {
    method: 'POST',
    body: JSON.stringify({ jti: String(jti || '') }),
  })
  if (unauthorized) return { ok: false, unauthorized: true, res, data }
  return { ok: !!(res.ok && data && data.ok), res, data, unauthorized: false }
}

/**
 * GET /auth/check — 200 means session ok (Caddy forward_auth + local).
 * redirect:'manual' so 302 is not followed; Safari may surface status 0 / opaqueredirect.
 */
export async function checkAuth() {
  const res = await fetch('/auth/check', {
    credentials: 'include',
    cache: 'no-store',
    redirect: 'manual',
  })
  if (res.status === 200) {
    return { ok: true, user: res.headers.get('X-Lunes-User') || null }
  }
  return { ok: false, user: null }
}


/** GET /api/paper */
export async function getPaper({
  betsOffset = 0,
  betsLimit = BETS_PAGE,
  horizon = 'all',
  asset = 'all',
  day = '',
  month = '',
} = {}) {
  const q = new URLSearchParams({
    bets_offset: String(betsOffset),
    bets_limit: String(betsLimit),
    horizon: String(horizon || 'all'),
    asset: String(asset || 'all'),
  })
  if (day) q.set('day', String(day))
  if (month) q.set('month', String(month))
  return apiFetch(`/api/paper?${q}`)
}

/** GET /api/paper/balance-series — equity path for one coin×TF. */
export async function getPaperBalanceSeries({
  asset = 'btc',
  horizon = '15m',
  period = 'day',
  day = '',
} = {}) {
  const q = new URLSearchParams({
    asset: String(asset || 'btc'),
    horizon: String(horizon || '15m'),
    period: String(period || 'day'),
  })
  if (day) q.set('day', String(day))
  return apiFetch(`/api/paper/balance-series?${q}`)
}

/** GET /api/live/bets — live feed for one MSK day + calendar (not paper). */
export async function getLiveBets({ day = '', month = '', betsLimit = 50 } = {}) {
  const q = new URLSearchParams({
    bets_limit: String(betsLimit),
  })
  if (day) q.set('day', String(day))
  if (month) q.set('month', String(month))
  return apiFetch(`/api/live/bets?${q}`)
}

/** GET /api/paper/brain — decision graph snapshot */
export async function getPaperBrain() {
  return apiFetch('/api/paper/brain')
}

/**
 * POST /api/paper/strategies/enable — toggle multi_strat unit (coin×TF).
 * Restarts matching paper unit only; does not wipe bankroll.
 * @param {string} horizon
 * @param {string} strategyId
 * @param {boolean} enabled
 * @param {string} [asset='btc']
 */
export async function setStrategyEnabled(horizon, strategyId, enabled, asset = 'btc') {
  const { res, data, unauthorized } = await apiFetch('/api/paper/strategies/enable', {
    method: 'POST',
    body: JSON.stringify({
      horizon: String(horizon || ''),
      strategy_id: String(strategyId || ''),
      enabled: !!enabled,
      asset: String(asset || 'btc'),
    }),
  })
  if (unauthorized) return { ok: false, error: 'unauthorized', res, data }
  const ok = !!(res.ok && data && data.ok)
  return {
    ok,
    error: (data && data.error) || (!ok ? `enable_failed_${res.status}` : null),
    res,
    data,
  }
}

/** GET /api/live/strategies — Live 15m per-strategy stats + enable flags */
export async function getLiveStrategies() {
  return apiFetch('/api/live/strategies')
}

/**
 * POST /api/live/strategies/enable — toggle strategies[].enabled AND
 * live.multi_strat.disabled on /var/lib/lunes-crypto/live/eth-15m.yaml
 * (same inode as config/live/eth-15m.yaml). Restarts pmbot-live-15m only
 * if that unit is already running; a paused coin does not start live trading.
 */
export async function setLiveStrategyEnabled(strategyId, enabled) {
  const { res, data, unauthorized } = await apiFetch('/api/live/strategies/enable', {
    method: 'POST',
    body: JSON.stringify({
      horizon: '15m',
      strategy_id: String(strategyId || ''),
      enabled: !!enabled,
    }),
  })
  if (unauthorized) return { ok: false, error: 'unauthorized', res, data }
  const ok = !!(res.ok && data && data.ok)
  return {
    ok,
    error: (data && data.error) || (!ok ? `enable_failed_${res.status}` : null),
    res,
    data,
  }
}

/**
 * POST /api/live/dogon/enable — toggle strategy.ensemble_tiered.dogon.enabled
 * on the same live yaml inode the unit reads. Disable also resets dogon hole.
 */
export async function setLiveDogonEnabled(enabled) {
  const { res, data, unauthorized } = await apiFetch('/api/live/dogon/enable', {
    method: 'POST',
    body: JSON.stringify({ enabled: !!enabled }),
  })
  if (unauthorized) return { ok: false, error: 'unauthorized', res, data }
  const ok = !!(res.ok && data && data.ok)
  return {
    ok,
    error: (data && data.error) || (!ok ? `enable_failed_${res.status}` : null),
    res,
    data,
  }
}

/** GET /api/paper/trading — per-coin arm/disarm (both TFs). */
export async function getPaperTrading() {
  return apiFetch('/api/paper/trading')
}

/**
 * POST /api/paper/trading/enable — start/stop both paper units for one coin.
 * @param {string} asset
 * @param {boolean} enabled
 */
export async function setPaperTradingEnabled(asset, enabled) {
  const { res, data, unauthorized } = await apiFetch('/api/paper/trading/enable', {
    method: 'POST',
    body: JSON.stringify({
      asset: String(asset || '').toLowerCase(),
      enabled: !!enabled,
    }),
  })
  if (unauthorized) return { ok: false, error: 'unauthorized', res, data }
  const ok = !!(res.ok && data && data.ok)
  return {
    ok,
    error: (data && data.error) || (!ok ? `trading_failed_${res.status}` : null),
    res,
    data,
  }
}

/** GET /api/live/trading — per-asset live arm/disarm. */
export async function getLiveTrading() {
  return apiFetch('/api/live/trading')
}

/**
 * POST /api/live/trading/enable — start/stop live unit(s) for one asset.
 * @param {string} asset
 * @param {boolean} enabled
 */
export async function setLiveTradingEnabled(asset, enabled) {
  const { res, data, unauthorized } = await apiFetch('/api/live/trading/enable', {
    method: 'POST',
    body: JSON.stringify({
      asset: String(asset || 'btc').toLowerCase(),
      enabled: !!enabled,
    }),
  })
  if (unauthorized) return { ok: false, error: 'unauthorized', res, data }
  const ok = !!(res.ok && data && data.ok)
  return {
    ok,
    error: (data && data.error) || (!ok ? `trading_failed_${res.status}` : null),
    error_ru: (data && (data.error_ru || data.note_ru)) || null,
    res,
    data,
  }
}

/** GET /api/ces-live — AI CES ETH 15m/5m wanted + equity. */
export async function getCesLive() {
  return apiFetch('/api/ces-live')
}

/**
 * POST /api/ces-live/enable — CES live wanted ON/OFF.
 * @param {string} market
 * @param {boolean} enabled
 */
export async function setCesLiveEnabled(market, enabled) {
  const { res, data, unauthorized } = await apiFetch('/api/ces-live/enable', {
    method: 'POST',
    body: JSON.stringify({
      market: String(market || '').toLowerCase(),
      enabled: !!enabled,
    }),
  })
  if (unauthorized) return { ok: false, error: 'unauthorized', res, data }
  const ok = !!(res.ok && data && data.ok)
  return {
    ok,
    error: (data && data.error) || (!ok ? `ces_toggle_failed_${res.status}` : null),
    error_ru: (data && (data.error_ru || data.note_ru)) || null,
    res,
    data,
  }
}

/** GET /api/paper/digest */
export async function getPaperDigest({ horizon = 'all' } = {}) {
  const q = new URLSearchParams({ horizon: String(horizon || 'all') })
  return apiFetch(`/api/paper/digest?${q}`)
}

/** GET /api/collect/health */
export async function getCollectHealth() {
  return apiFetch('/api/collect/health')
}

/** GET /api/ops/status */
export async function getOpsStatus() {
  return apiFetch('/api/ops/status')
}

/**
 * Normalize ops status units[] — server uses `unit` (not `name`) and `main_pid`.
 * @returns {{ overall: string, units: Array<{unit:string,ok:boolean,healthy?:boolean,active_state?:string,sub_state?:string,main_pid?:number|null,pid_alive?:boolean,mutable?:boolean,error?:string}>, controls: object, raw: object } | null}
 */
export function getBotStatus(opsPayload) {
  if (!opsPayload || !opsPayload.ok) return null
  const units = (opsPayload.units || []).map((u) => ({
    unit: u.unit || u.name || '?',
    ok: !!u.ok,
    healthy: !!u.healthy,
    active_state: u.active_state || '',
    sub_state: u.sub_state || '',
    main_pid: u.main_pid != null ? u.main_pid : null,
    pid_alive: !!u.pid_alive,
    mutable: !!u.mutable,
    n_restarts: u.n_restarts,
    result: u.result || '',
    error: u.error || null,
  }))
  return {
    overall: opsPayload.overall || 'unknown',
    units,
    controls: opsPayload.controls || {},
    generated_at_utc: opsPayload.generated_at_utc,
    raw: opsPayload,
  }
}

/**
 * POST /api/ops/control
 * Paper: confirm MUST come from user input — never auto-built here.
 * Live units: confirm optional (SSO + Origin + rate-limit only).
 */
export async function executeOpsCommand(action, confirm, { unit, mode = DEFAULT_MODE } = {}) {
  const u = unit || opsUnitForMode(mode)
  if (!u) {
    return { ok: false, error: 'unit_unavailable_for_mode', res: null, data: null }
  }
  const confirmTrim = typeof confirm === 'string' ? confirm.trim() : ''
  if (!isLiveOpsUnit(u) && !confirmTrim) {
    return { ok: false, error: 'confirm_required', res: null, data: null }
  }
  const body = {
    unit: u,
    action: String(action).toLowerCase(),
  }
  if (confirmTrim) body.confirm = confirmTrim
  else if (isLiveOpsUnit(u)) body.confirm = ''
  const { res, data, unauthorized } = await apiFetch('/api/ops/control', {
    method: 'POST',
    body: JSON.stringify(body),
  })
  if (unauthorized) return { ok: false, error: 'unauthorized', res, data }
  const ok = !!(res.ok && data && data.ok)
  return {
    ok,
    error: (data && data.error) || (!ok ? `ops_failed_${res.status}` : null),
    error_ru: (data && (data.error_ru || data.note_ru)) || null,
    res,
    data,
  }
}

/** Expected confirm token for UI hint only — never pass this to executeOpsCommand automatically. */
export function opsConfirmHint(action, unit = 'pmbot-paper') {
  return `${String(action).toUpperCase()} ${unit}`
}

/** GET /api/client/admin — operator compact client index (SSO). */
export async function getClientAdmin() {
  return apiFetch('/api/client/admin')
}

/**
 * GET /api/client/admin/accounts/:id — one client's live-parity panel.
 * @param {number} telegramId
 * @param {{ day?: string, month?: string }} [opts]
 */
export async function getClientAdminAccount(telegramId, opts = {}) {
  const q = new URLSearchParams()
  if (opts.day) q.set('day', String(opts.day))
  if (opts.month) q.set('month', String(opts.month))
  const qs = q.toString()
  const path = `/api/client/admin/accounts/${Number(telegramId)}${qs ? `?${qs}` : ''}`
  return apiFetch(path)
}

/**
 * POST /api/client/admin/accounts/:id/emergency — stop + revoke whitelist.
 * @param {number} telegramId
 */
export async function clientAdminEmergency(telegramId) {
  const { res, data, unauthorized } = await apiFetch(
    `/api/client/admin/accounts/${Number(telegramId)}/emergency`,
    {
      method: 'POST',
      body: JSON.stringify({ confirm: true }),
    },
  )
  if (unauthorized) return { ok: false, error: 'unauthorized', res, data }
  const ok = !!(res.ok && data && data.ok)
  return {
    ok,
    error: (data && data.error) || (!ok ? `emergency_failed_${res.status}` : null),
    res,
    data,
  }
}

/**
 * POST /api/client/admin/accounts/:id/password-reset — clear Mini App hash.
 * @param {number} telegramId
 */
export async function clientAdminPasswordReset(telegramId) {
  const { res, data, unauthorized } = await apiFetch(
    `/api/client/admin/accounts/${Number(telegramId)}/password-reset`,
    {
      method: 'POST',
      body: JSON.stringify({ confirm: true }),
    },
  )
  if (unauthorized) return { ok: false, error: 'unauthorized', res, data }
  const ok = !!(res.ok && data && data.ok)
  return {
    ok,
    error: (data && data.error) || (!ok ? `password_reset_failed_${res.status}` : null),
    res,
    data,
  }
}

/**
 * POST /api/client/admin/accounts/:id/strategies/enable — friend's yaml only.
 * @param {number} telegramId
 * @param {string} strategyId
 * @param {boolean} enabled
 */
export async function setClientStrategyEnabled(telegramId, strategyId, enabled) {
  const { res, data, unauthorized } = await apiFetch(
    `/api/client/admin/accounts/${Number(telegramId)}/strategies/enable`,
    {
      method: 'POST',
      body: JSON.stringify({
        strategy_id: String(strategyId || ''),
        enabled: !!enabled,
      }),
    },
  )
  if (unauthorized) return { ok: false, error: 'unauthorized', res, data }
  const ok = !!(res.ok && data && data.ok)
  return {
    ok,
    error: (data && data.error) || (!ok ? `enable_failed_${res.status}` : null),
    res,
    data,
  }
}

/**
 * POST /api/client/admin/accounts/:id/trading/enable — friend's halt + client unit.
 * Never Georg pmbot-live-15m. Same accounts.json as Mini App.
 * @param {number} telegramId
 * @param {boolean} enabled
 */
export async function setClientTradingEnabled(telegramId, enabled) {
  const { res, data, unauthorized } = await apiFetch(
    `/api/client/admin/accounts/${Number(telegramId)}/trading/enable`,
    {
      method: 'POST',
      body: JSON.stringify({ enabled: !!enabled }),
    },
  )
  if (unauthorized) return { ok: false, error: 'unauthorized', res, data }
  const ok = !!(res.ok && data && data.ok)
  return {
    ok,
    error: (data && data.error) || (!ok ? `enable_failed_${res.status}` : null),
    error_ru: (data && (data.error_ru || data.note_ru)) || null,
    res,
    data,
  }
}

/** GET /api/ops/audit?tail≤50 */
export async function getOpsAudit(tail = 50) {
  const t = Math.max(1, Math.min(Number(tail) || 50, 50))
  return apiFetch(`/api/ops/audit?tail=${t}`)
}

/** GET /api/ops/journal */
export async function getOpsJournal({ unit = 'pmbot-paper', lines = 40 } = {}) {
  const q = new URLSearchParams({ unit, lines: String(lines) })
  return apiFetch(`/api/ops/journal?${q}`)
}

export { BETS_PAGE, push, replace, loginUrl, spaPath, landingUrl }
